import bedcaFoodsCatalog from "./data/bedcaFoodsCatalog.json";
import {
  CATALOG_FOOD_KIND_BRANDED,
  CATALOG_FOOD_SOURCE_EFORT,
  CATALOG_FOOD_SOURCE_OPEN_SOURCE,
  normalizeCatalogFoodKind,
  normalizeCatalogFoodSource,
} from "./supabaseFoodCatalogShared";

export const FOOD_LAZY_BATCH_SIZE = 40;
export const FOOD_LAZY_LOAD_DELAY_MS = 120;
export const FOOD_SENTINEL_ROOT_MARGIN = "0px 0px 320px 0px";

const FOOD_TYPE_FOOD = "food";
const FOOD_TYPE_SUPPLEMENT = "supplement";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function normalizeLanguage(language) {
  return String(language || "en").toLowerCase();
}

function normalizeBundledCatalogFood(food) {
  if (!food || typeof food !== "object") {
    return food;
  }

  return {
    ...food,
    source: normalizeCatalogFoodSource(food?.source, {
      defaultSource: CATALOG_FOOD_SOURCE_OPEN_SOURCE,
    }),
    _catalog: true,
    _readOnly: true,
  };
}

export const BEDCA_FOOD_CATALOG = Object.freeze(
  (Array.isArray(bedcaFoodsCatalog) ? bedcaFoodsCatalog : []).map(
    normalizeBundledCatalogFood,
  ),
);

export function isCatalogFood(food) {
  return food?._catalog === true;
}

export function hasExplicitFoodType(food) {
  return (
    food?.type === FOOD_TYPE_FOOD || food?.type === FOOD_TYPE_SUPPLEMENT
  );
}

export function getResolvedFoodType(food) {
  if (food?.type === FOOD_TYPE_SUPPLEMENT) {
    return FOOD_TYPE_SUPPLEMENT;
  }

  if (food?.isSupplement === true) {
    return FOOD_TYPE_SUPPLEMENT;
  }

  return FOOD_TYPE_FOOD;
}

export function isSupplementFood(food) {
  return getResolvedFoodType(food) === FOOD_TYPE_SUPPLEMENT;
}

export function getLocalizedFoodName(food, language = "en") {
  const normalizedLanguage = normalizeLanguage(language);
  const prefersSpanish = normalizedLanguage.startsWith("es");
  const primaryName = prefersSpanish ? food?.name_es : food?.name_en;
  const fallbackName = prefersSpanish ? food?.name_en : food?.name_es;
  const genericName = food?.name;

  if (isCatalogFood(food)) {
    if (hasText(primaryName)) {
      return primaryName.trim();
    }

    if (hasText(genericName)) {
      return genericName.trim();
    }

    if (hasText(fallbackName)) {
      return fallbackName.trim();
    }

    return "";
  }

  if (hasText(genericName)) {
    return genericName.trim();
  }

  if (hasText(primaryName)) {
    return primaryName.trim();
  }

  if (hasText(fallbackName)) {
    return fallbackName.trim();
  }

  return "";
}

export function getCatalogSourceLabel(food, t) {
  if (!isCatalogFood(food)) {
    return "";
  }

  const catalogKind = normalizeCatalogFoodKind(
    food?.catalog_kind ?? food?.catalogKind,
    food,
  );
  if (catalogKind === CATALOG_FOOD_KIND_BRANDED) {
    if (hasText(food?.brand)) {
      return food.brand.trim();
    }

    if (hasText(food?.store)) {
      return food.store.trim();
    }
  }

  const source = normalizeCatalogFoodSource(food?.source, {
    defaultSource: CATALOG_FOOD_SOURCE_EFORT,
  });

  if (source === CATALOG_FOOD_SOURCE_EFORT) {
    return typeof t === "function"
      ? t("catalogSourceEfort-string", "EFORT")
      : "EFORT";
  }

  return typeof t === "function"
    ? t("catalogSourceOpenSource-string", "OPEN SOURCE")
    : "OPEN SOURCE";
}

export function getFoodSearchText(food, language = "en", t) {
  const localizedName = getLocalizedFoodName(food, language);
  const catalogSourceLabel = getCatalogSourceLabel(food, t);

  return [
    localizedName,
    food?.name,
    food?.name_es,
    food?.name_en,
    food?.brand,
    food?.store,
    food?.per,
    food?.notes,
    catalogSourceLabel,
    food?.source,
  ]
    .filter(hasText)
    .join(" ")
    .toLowerCase();
}

export function matchesFoodSearch(food, searchText, language = "en", t) {
  const normalizedSearch = String(searchText || "")
    .trim()
    .toLowerCase();

  if (normalizedSearch.length === 0) {
    return true;
  }

  return getFoodSearchText(food, language, t).includes(normalizedSearch);
}

export function getFoodNameForSort(food, language = "en") {
  return getLocalizedFoodName(food, language).toLowerCase();
}

export function getNextVisibleCount(
  totalCount,
  currentVisibleCount,
  batchSize = FOOD_LAZY_BATCH_SIZE,
) {
  if (!Number.isFinite(totalCount) || totalCount <= 0) {
    return 0;
  }

  const safeCurrentVisibleCount = Math.max(0, Number(currentVisibleCount) || 0);
  const safeBatchSize = Math.max(1, Number(batchSize) || FOOD_LAZY_BATCH_SIZE);

  return Math.min(totalCount, safeCurrentVisibleCount + safeBatchSize);
}
